import { useMemo, useSyncExternalStore } from "react"
import { Outlet, useParams } from "react-router-dom"

import { useServices } from "@/hooks/useServices"
import { useAgentsAI } from "@/pages/DiscoveryPage/application/ai-discovery/useAiDiscovery"
import {
  newAgentPolicy,
  toPolicyGroup
} from "@/pages/GovernancePage/applications/policies/agentPolicy"
import { getOptions } from "@/pages/GovernancePage/applications/policies/getDemoData"
import {
  AI_POLICY_FEATURES,
  AiPoliciesContext,
  PENDING_AI_POLICIES_CONTEXT
} from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import {
  AGENT_POLICIES_HEADING_KEY,
  ALL_AGENTS_HEADING_KEY,
  ENVIRONMENT_POLICIES_HEADING_KEY,
  getData,
  toRuleGroups
} from "./getData"
import { readStore, subscribeStore, writeStore } from "./store"

import type {
  AgentRecord,
  Record
} from "@/pages/GovernancePage/applications/policies/getDemoData.types"
import type {
  HardeningAgent,
  HardeningSuggestion
} from "@/pages/GovernancePage/applications/policies/getHardeningSuggestions.types"
import type {
  AiPoliciesContextValue,
  PolicyRecordInput
} from "@/pages/GovernancePage/applications/policies/useAiPolicies.types"
import type { AiPolicyFormValues } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/AiPolicyForm/AiPolicyForm.types"

const PLAYGROUND_AGENT_NAMES = [
  "TriageAgent",
  "FixAgent",
  "ReporterAgent",
  "DedupeAgent",
  "ReviewAgent",
  "ResearchAgent",
  "Web Researcher"
]
const RULE_OPTIONS = getOptions()

const bumpHeading = (records: Record[], key: string) =>
  records.map((item) =>
    item.key === key
      ? { ...item, count: item.count + 1, total: item.total + 1 }
      : item
  )

const applyFormValues = <R extends PolicyRecordInput>(
  record: R,
  values: AiPolicyFormValues
): R => ({
  ...record,
  // The drawer edits rules only, so the name and description fields are absent.
  policyName: values.name_details ?? record.policyName,
  policyDescription: values.description_details ?? record.policyDescription,
  allowed: toRuleGroups(values.allow),
  notAllowed: toRuleGroups(values.notAllow)
})

export const AiPolicyProvider = (props: {
  children?: React.ReactNode
  mode?: "view" | "edit"
  id?: string
  agentName?: string
}) => {
  const { auth } = useServices()
  const company = auth.getCompany() || "default"
  const { response, isPending } = useAgentsAI()
  const params = useParams<{ id: string; mode?: "view" | "edit" }>()
  const id = props.id ?? params.id
  const mode = props.mode ?? params.mode
  const { agentName } = props
  const isSingleView =
    (!!id && (mode === "edit" || mode === "view")) || !!agentName

  const { data } = response || {}
  // Seeds are derived from the tenant's agents until the user changes something;
  // every change commits to the shared store, which every provider on the page
  // (list page, agent drawer, agents table) reads and re-renders from.
  const store = useSyncExternalStore(subscribeStore, () => readStore(company))

  const value = useMemo<AiPoliciesContextValue>(() => {
    if (isPending || !data) return PENDING_AI_POLICIES_CONTEXT

    const agentsByPolicies = data.reduce(
      (acc, item) => {
        if (item.name === "RemediationLead") acc.agents[item.name] = item
        else if (PLAYGROUND_AGENT_NAMES.includes(item.name))
          acc.playground.push({
            environment: "Playground",
            violated: false,
            ...item,
            apps: {
              ...item.apps,
              items: (item.apps?.items ?? []).map((app) => ({
                ...app,
                description: app.description.replace("production", "playground")
              }))
            }
          })
        else
          acc.all.push({
            environment: "Production",
            violated: false,
            ...item
          })

        return acc
      },
      {
        all: [] as ((typeof data)[number] & {
          environment: string
          violated: boolean
        })[],
        playground: [] as ((typeof data)[number] & {
          environment: string
          violated: boolean
        })[],
        agents: {} as { [key in string]: (typeof data)[number] }
      }
    )

    const records: Record[] =
      store?.records ?? getData<(typeof data)[number]>(agentsByPolicies)
    const commit = (nextRecords: Record[]) => {
      writeStore(company, { records: nextRecords })
    }
    const findById = (needle: string) =>
      records.find((item) => `${item.id}` === needle)
    const findAgentPolicy = (name: string) =>
      records.find(
        (item): item is AgentRecord =>
          item.type === "agent" && item.name === name
      )
    // A hand-created "All agents" scope applies to the same agents as the seed.
    const withScopeAgents = <R extends PolicyRecordInput>(record: R): R => {
      if (record.type !== "scope" || record.agents.length > 0) return record

      const agents = [...agentsByPolicies.all, ...agentsByPolicies.playground]

      return {
        ...record,
        agents,
        count: agents.length,
        total: agents.length,
        description: `${agents.length} agents`,
        agentsDescription: `${agents.length} agents run these rules.`
      }
    }

    let nextData = isSingleView
      ? records.filter((item) => `${item.id}` === id)
      : records

    if (isSingleView && !id) {
      const agentRecords = records.filter(
        (item) => item.type === "agent" && item.name === agentName
      )
      const scopeRecords = records.filter(
        (item) =>
          item.type === "scope" &&
          item.agents.some((agent) => agent.name === agentName)
      )

      nextData = agentRecords.length > 0 ? agentRecords : scopeRecords
    }

    const update: AiPoliciesContextValue["update"] = (needle, values) => {
      const record = findById(needle)

      if (!record || record.type === "heading") return

      commit(
        records.map((item) =>
          item === record ? applyFormValues(record, values) : item
        )
      )
    }

    const create: AiPoliciesContextValue["create"] = (record, values) => {
      const needle = `${record.id}`

      if (findById(needle)) {
        update(needle, values)

        return needle
      }

      const saved = applyFormValues(withScopeAgents(record), values)

      if (record.type === "agent") {
        commit([...bumpHeading(records, AGENT_POLICIES_HEADING_KEY), saved])

        return needle
      }

      // Scope policies sit under the first heading, before the environment block.
      const bumped = bumpHeading(records, ALL_AGENTS_HEADING_KEY)
      const index = bumped.findIndex(
        (item) => item.key === ENVIRONMENT_POLICIES_HEADING_KEY
      )

      commit(
        index === -1
          ? [...bumped, saved]
          : [...bumped.slice(0, index), saved, ...bumped.slice(index)]
      )

      return needle
    }

    // A drawer edit becomes the agent's own policy: it updates the agent's
    // record, or creates one from the edited (inherited) rules. The inherited
    // policy is never changed from the drawer.
    const saveAgentPolicy: AiPoliciesContextValue["saveAgentPolicy"] = (
      agent,
      values
    ) => {
      const own = findAgentPolicy(agent.name)

      if (own) {
        update(`${own.id}`, values)

        return `${own.id}`
      }

      return create(newAgentPolicy(agent), values)
    }

    const buildAgentPolicy = (
      agent: HardeningAgent,
      picked: HardeningSuggestion[]
    ): AgentRecord => {
      const own = findAgentPolicy(agent.name)
      const base = own ?? newAgentPolicy(agent)
      const groups = (side: "allow" | "notAllow") =>
        picked.filter((item) => item.side === side).map(toPolicyGroup)

      return {
        ...base,
        allowed: [...base.allowed, ...groups("allow")],
        notAllowed: [...base.notAllowed, ...groups("notAllow")]
      }
    }

    const approveSuggestions: AiPoliciesContextValue["approveSuggestions"] = (
      agent,
      picked
    ) => {
      const built = buildAgentPolicy(agent, picked)
      const exists = records.some((item) => item.id === built.id)

      commit(
        exists
          ? records.map((item) => (item.id === built.id ? built : item))
          : [...bumpHeading(records, AGENT_POLICIES_HEADING_KEY), built]
      )

      return built
    }

    return {
      source: "demo",
      features: AI_POLICY_FEATURES.demo,
      ruleOptions: RULE_OPTIONS,
      isPending,
      mode,
      response: { data: nextData },
      update,
      create,
      findById,
      findAgentPolicy,
      saveAgentPolicy,
      approveSuggestions
    }
  }, [isPending, data, store, company, isSingleView, mode, id, agentName])

  return (
    <AiPoliciesContext.Provider value={value}>
      {props.children ?? <Outlet />}
    </AiPoliciesContext.Provider>
  )
}
